/**
 * The customer funnel, as conversion events.
 *
 * Everything the site tracked before this module was a click or a page view, so
 * Google Ads could only bid towards people who press buttons. These events are
 * the milestones that mean a customer:
 *
 *   sign_up             a Cognito registration completed
 *   trial_started       a trial account exists and is active
 *   project_created     the account created its first project
 *   api_key_created     the account created its first CLI credential
 *   activation_complete the account has both, so the CLI will actually run
 *   purchase            Stripe Checkout completed, with the annual value
 *
 * Each one fires to gtag (GA4 + Google Ads) and to PostHog.
 *
 * Alongside them, `recordFunnelEvent` carries the funnel-step and intent events
 * that explain the numbers — which step a visitor reached, which command they
 * copied. Those go to GA4 and PostHog only; see FunnelEventName in analytics.ts
 * for why they must never reach Ads as conversions.
 *
 * ── Firing exactly once ────────────────────────────────────────────────────────
 * None of these events is triggered by a click, which is what makes duplicates
 * likely. They are triggered by *observations*: a render, an effect, an API
 * response, a URL the browser was redirected to. All of those repeat — React
 * StrictMode double-invokes effects, remounting a route re-runs them, and the
 * Stripe success URL is a normal page a visitor can reload or bookmark.
 *
 * So no event is keyed on "has this code run", it is keyed on the *identity of
 * the thing that happened*: the Stripe Checkout Session ID, the trial's
 * server-assigned start timestamp, the user ID, the registered email. `claimOnce`
 * hands out each identity exactly one time, and remembers in localStorage so the
 * claim survives a reload and the cross-site round trip through Stripe.
 */

import { ConversionEventName, EventParams, FunnelEventName, sendGtagConversion, trackEvent } from './analytics';
import { posthog } from './posthog';

/** Everything this module can claim an identity for. */
export type TrackedEventName = ConversionEventName | FunnelEventName;

/** Prefix for the localStorage entries that record which conversions have fired. */
const CLAIM_PREFIX = 'localingos.conversion.';

/** Where a purchase's value is parked while the visitor is away at Stripe. */
const PENDING_PURCHASE_KEY = 'localingos.pendingPurchase';

/**
 * How recently a trial must have started for us to call it a new trial.
 *
 * `trial_started` is derived from observing an active trial rather than from
 * watching one be created, because trials are created server-side (TrialService's
 * getOrCreateTrialAccount) on the account's first billing call, which the browser
 * never sees directly. Without a freshness bound, an eighteen-month-old trial
 * user opening the dashboard in a new browser would report a fresh trial. A real
 * signup reaches GET /billing/subscription seconds after the trial row is written.
 */
const TRIAL_FRESHNESS_WINDOW_MS = 30 * 60 * 1000;

/**
 * Claims already handed out in this page's lifetime.
 *
 * Checked before localStorage so the guard still holds when localStorage throws
 * (private browsing, blocked cookies) — in that case duplicates are suppressed
 * within the page view, which is where re-renders and StrictMode live, and only a
 * genuine reload could produce a second event.
 */
const claimedInThisPage = new Set<string>();

/**
 * True the first time this key is seen, false forever after.
 *
 * Exported for tests; production code should call one of the record* functions.
 */
export function claimOnce(event: TrackedEventName, identity: string): boolean {
    const key = `${CLAIM_PREFIX}${event}.${identity}`;

    if (claimedInThisPage.has(key)) return false;
    claimedInThisPage.add(key);

    try {
        if (localStorage.getItem(key) !== null) return false;
        localStorage.setItem(key, new Date().toISOString());
    } catch {
        // No durable store. The in-memory claim above still covers this page view.
    }

    return true;
}

/**
 * Whether this identity has already been claimed, without claiming it.
 *
 * Read by `recordActivationComplete`, which needs to know whether *this browser*
 * watched part of the activation happen. Deliberately not a general-purpose
 * "has this fired" query: anything that wants to fire should call `claimOnce`,
 * which decides and records atomically.
 */
function wasClaimed(event: TrackedEventName, identity: string): boolean {
    const key = `${CLAIM_PREFIX}${event}.${identity}`;

    if (claimedInThisPage.has(key)) return true;

    try {
        return localStorage.getItem(key) !== null;
    } catch {
        return false;
    }
}

/**
 * Claims for events that describe a visit rather than an account.
 *
 * `claimOnce` persists to localStorage, which is exactly right for an account
 * milestone — a purchase must never be reported twice, even after a reload — and
 * exactly wrong for funnel progress. If "reached step 3" were remembered
 * forever, a visitor who came back a week later and got stuck at step 3 again
 * would vanish from the funnel report rather than showing up as a second attempt.
 *
 * So this guard keeps the part that matters (a re-render, a remount and a
 * StrictMode double-effect all see the same in-memory Set, because it lives at
 * module scope rather than in a component) and drops the durability: a genuine
 * new page view is a genuine new observation and is counted again.
 */
const claimedInThisPageView = new Set<string>();

export function claimOncePerPageView(event: FunnelEventName, identity: string): boolean {
    const key = `${event}.${identity}`;

    if (claimedInThisPageView.has(key)) return false;
    claimedInThisPageView.add(key);

    return true;
}

/** Test seam: forgets this page's in-memory claims. */
export function resetClaimsForTesting(): void {
    claimedInThisPage.clear();
    claimedInThisPageView.clear();
}

/** Hands an event to PostHog, if it is loaded and the visitor has not opted out. */
function capturePostHog(event: TrackedEventName, params: EventParams): void {
    try {
        posthog.capture(event, params);
    } catch {
        // PostHog not loaded or opted out — GA4 already has the event.
    }
}

/** Fires one conversion to gtag (GA4 + Ads) and to PostHog. */
function fire(event: ConversionEventName, params: EventParams): void {
    sendGtagConversion(event, params);
    capturePostHog(event, params);
}

/**
 * Fires one funnel or intent event to GA4 and PostHog.
 *
 * Routed through `trackEvent` rather than `sendGtagConversion` on purpose: these
 * events must reach the analytics property but must never be sent to Google Ads
 * as a conversion. Bidding towards "copied a command" would buy clicks from
 * people who copy commands, which is the mistake this whole module exists to
 * undo.
 */
export function recordFunnelEvent(event: FunnelEventName, params: EventParams = {}): void {
    trackEvent(event, params);
    capturePostHog(event, params);
}

// ── The authenticated user ────────────────────────────────────────────────────

/**
 * The Cognito `sub` of the signed-in user, published by UserContext.
 *
 * `project_created` needs it to key its once-per-account claim, and it is
 * observed from an axios interceptor that has no access to React context.
 */
let currentUserId: string | undefined;

export function setConversionUserId(userId: string | undefined): void {
    currentUserId = userId;
}

// ── sign_up ───────────────────────────────────────────────────────────────────

/**
 * A Cognito registration completed.
 *
 * Called from LocalingosAuthenticator at the two points where an account
 * genuinely exists and is usable: a `signUp()` that came back already complete,
 * and a successful `confirmSignUp()`. Not from the "Create account" button, which
 * is only an intent, and not from the Google button — see recordSignUp's caller
 * for why that path cannot be identified from the browser.
 *
 * Keyed on the email, which is the Cognito username: retrying confirmation with a
 * fresh code, or reloading mid-flow, must not produce a second signup.
 */
export function recordSignUp(email: string, method: 'email' | 'google'): void {
    const identity = email.trim().toLowerCase();
    if (!identity) return;
    if (!claimOnce('sign_up', identity)) return;

    fire('sign_up', { method });
}

// ── trial_started ─────────────────────────────────────────────────────────────

/** The fields of BillingSubscriptionDto that decide whether a trial just started. */
export interface TrialObservation {
    billingAccountId?: string;
    onTrial?: boolean;
    trialStatus?: string;
    trialStartedAt?: string;
}

/**
 * An active trial was observed on a GET /billing/subscription response.
 *
 * Keyed on the billing account plus the server's `trialStartedAt`, so the same
 * trial can never be reported twice however many times the subscription is
 * fetched, and a genuinely new trial on the same account (were one ever issued)
 * would still count.
 */
export function recordTrialStarted(subscription: TrialObservation | null | undefined): void {
    if (!subscription) return;
    if (subscription.onTrial !== true || subscription.trialStatus !== 'ACTIVE') return;

    const { billingAccountId, trialStartedAt } = subscription;
    if (!billingAccountId || !trialStartedAt) return;

    const startedAt = Date.parse(trialStartedAt);
    if (Number.isNaN(startedAt)) return;

    const age = Date.now() - startedAt;
    if (age < 0 || age > TRIAL_FRESHNESS_WINDOW_MS) return;

    if (!claimOnce('trial_started', `${billingAccountId}:${trialStartedAt}`)) return;

    // Trial terms come from the backend's TrialConfig (14 days, 5,000 words) and
    // are sent as parameters so a change there is visible in the funnel reports
    // rather than silently baked into a conversion name.
    fire('trial_started', { trial_days: 14, trial_words: 5000 });
}

// ── project_created ───────────────────────────────────────────────────────────

/**
 * The account's first project was created.
 *
 * Keyed on the user, not the project: the activation milestone Google Ads should
 * bid towards is "this account started using the product at all", and every
 * project after the first tells it nothing new about the click that won the
 * account. Falls back to a single shared key when the user ID has not arrived
 * yet, which cannot happen in practice — POST /project requires a session, and
 * UserContext publishes the ID as soon as one exists.
 */
export function recordProjectCreated(projectId?: string): void {
    if (!claimOnce('project_created', currentUserId ?? 'unknown-user')) return;

    fire('project_created', { project_id: projectId });
}

// ── api_key_created ───────────────────────────────────────────────────────────

/**
 * The account's first API key was created.
 *
 * An API key has exactly one purpose — authenticating the CLI — so creating one
 * is the least ambiguous statement of intent the website can observe. Nobody
 * generates a CLI credential to browse.
 *
 * Observed from the response interceptor rather than from a component: /get-started
 * has an inline create form and /api-keys is a whole page of its own, and both are
 * the same fact about the account. Keyed on the user rather than on the key, for
 * the same reason `project_created` is: the milestone is "this account can now run
 * the CLI", and the second and third key say nothing further about the click that
 * won the account.
 *
 * The key's value is never sent as a parameter. It is a live credential.
 */
export function recordApiKeyCreated(): void {
    if (!claimOnce('api_key_created', currentUserId ?? 'unknown-user')) return;

    fire('api_key_created', {});
}

// ── activation_complete ───────────────────────────────────────────────────────

/**
 * The account has both a project and an API key: it can actually run `localingos sync`.
 *
 * ── Why this is guarded twice ──────────────────────────────────────────────────
 * Unlike every other conversion here, this one is derived from a *state* rather
 * than from an event. Nothing announces "you are now activated"; the page simply
 * notices that both things exist. State is the dangerous kind of observation,
 * because a customer who activated eighteen months ago still satisfies it — and
 * the once-per-account claim below would happily report them as a fresh
 * activation the first time they open the page in a new browser, since the claim
 * lives in that browser's localStorage and a new browser has none.
 *
 * That failure mode is not a rounding error. This event is meant to become the
 * primary Google Ads conversion, and a primary conversion that fires for existing
 * customers teaches the bidder to buy traffic that looks like existing customers.
 *
 * So it fires only when this browser also holds the claim for the account's
 * `project_created` or `api_key_created`, i.e. only when we watched at least half
 * of the activation happen. A genuine new customer always satisfies that — they
 * created the project or the key here, minutes ago. A returning long-standing
 * customer never does.
 *
 * The cost is a false negative: an account that creates its project and key on one
 * device and first loads /get-started on another is not reported. That is the
 * right way round for a bidding signal, and it is the trade the freshness window
 * on `trial_started` makes too.
 */
export function recordActivationComplete(): void {
    const identity = currentUserId ?? 'unknown-user';

    const watchedItHappen =
        wasClaimed('project_created', identity) || wasClaimed('api_key_created', identity);
    if (!watchedItHappen) return;

    if (!claimOnce('activation_complete', identity)) return;

    fire('activation_complete', {});
}

// ── purchase ──────────────────────────────────────────────────────────────────

/** What a purchase is worth, captured before the visitor leaves for Stripe. */
export interface PendingPurchase {
    planKey: string;
    /** Annual price in major units. All plans are billed annually. */
    value: number;
    currency: string;
}

/**
 * Remembers what the visitor is about to buy.
 *
 * Google Ads value-based bidding needs a real amount on the conversion, and the
 * success URL Stripe redirects to carries only a session ID. Reading the amount
 * back off the subscription instead would race the Stripe webhook that writes it,
 * so the price is captured here, at the click, where it is already known.
 *
 * sessionStorage rather than localStorage: it survives the round trip to
 * Stripe and back in the same tab, and evaporates if the visitor abandons
 * checkout, so a later unrelated visit cannot inherit a stale amount.
 */
export function stashPendingPurchase(pending: PendingPurchase): void {
    try {
        sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(pending));
    } catch {
        // Not storable. recordPurchase falls back to the subscription's price.
    }
}

/** Reads and clears the stashed purchase, if the visitor came back with one. */
export function takePendingPurchase(): PendingPurchase | undefined {
    try {
        const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
        if (!raw) return undefined;
        sessionStorage.removeItem(PENDING_PURCHASE_KEY);

        const parsed = JSON.parse(raw) as Partial<PendingPurchase>;
        if (typeof parsed.value !== 'number' || !Number.isFinite(parsed.value)) return undefined;
        if (typeof parsed.currency !== 'string' || !parsed.currency) return undefined;

        return { planKey: String(parsed.planKey ?? ''), value: parsed.value, currency: parsed.currency };
    } catch {
        return undefined;
    }
}

export interface PurchaseDetails {
    /** Stripe Checkout Session ID from the success URL. Also the dedupe key. */
    sessionId: string;
    planKey?: string;
    value?: number;
    currency?: string;
}

/**
 * Stripe Checkout completed.
 *
 * Keyed on the Checkout Session ID, which Stripe mints once per checkout, so the
 * bookmarkable `?success=true&session_id=…` URL can be reloaded, re-opened or
 * re-entered by a StrictMode double effect without billing a second conversion.
 * The same ID is sent as `transaction_id`, which gives GA4 and Google Ads their
 * own server-side deduplication on top of ours.
 */
export function recordPurchase({ sessionId, planKey, value, currency }: PurchaseDetails): void {
    if (!sessionId) return;
    if (!claimOnce('purchase', sessionId)) return;

    fire('purchase', {
        transaction_id: sessionId,
        value,
        currency,
        items_plan: planKey,
    });
}

// ── Observing API traffic ─────────────────────────────────────────────────────

/** Strips the origin, query and trailing slash so a path can be compared exactly. */
function normalisePath(url: string | undefined): string {
    if (!url) return '';

    let path = url;
    const schemeEnd = path.indexOf('://');
    if (schemeEnd !== -1) {
        const afterHost = path.indexOf('/', schemeEnd + 3);
        path = afterHost === -1 ? '/' : path.slice(afterHost);
    }

    path = path.split(/[?#]/)[0];
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

    return path;
}

/**
 * Turns a successful API response into a conversion, when it is one.
 *
 * Wired into apiClient's response interceptor rather than into the components
 * that make the calls. Two reasons: the moment a project exists is the moment the
 * server says so, not the moment a form's promise resolves in one particular
 * component, so every caller is covered by construction; and the trial is only
 * ever visible as a field on a subscription response, never as an event.
 */
export function observeApiResponse(
    method: string | undefined,
    url: string | undefined,
    status: number,
    data: unknown
): void {
    if (status < 200 || status >= 300) return;

    const verb = (method ?? 'get').toUpperCase();
    const path = normalisePath(url);

    if (verb === 'POST' && path === '/project') {
        const project = data as { id?: string } | undefined;
        recordProjectCreated(project?.id);
        return;
    }

    // Exact path, and POST only. `GET /api/api-keys` is the list the page loads on
    // mount — matching it would report an API key creation on every page view by
    // every account that already has one. `DELETE /api/api-keys/{apiKey}` is not
    // this path at all.
    if (verb === 'POST' && path === '/api/api-keys') {
        recordApiKeyCreated();
        return;
    }

    if (verb === 'GET' && path === '/billing/subscription') {
        recordTrialStarted(data as TrialObservation | undefined);
    }
}
